/* eslint-disable no-plusplus */
import { useMemo } from 'react';

import { Box, Skeleton } from '@chakra-ui/react';
import {
  useSelectDisciplinaryActionsSelectedCompanies,
  useSelectDisciplinaryActionsSelectedLocations,
} from 'Deer/intent/DisciplinaryActions/slice/DisciplinaryActionsIntentSlice';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { DISCIPLINARY_ACTIONS_METRIC_CARDS, MODULE_KEYS_METRIC_CARDS_DEER } from 'Deer/metricCards/constants';
import { StyledPaper, StyledTypography } from 'styles/common';
import { checkEnableCompanySupport } from 'utils/helpers/featureFlags';

const MetricCardFilters = ({ rows = [], isLoading, error, gridStatePersistenceKey }) => {
  const METRIC_CARD_HEADER_STATUS_VALUE_MAP = {
    AWAITING_ADMIN_APPROVAL: 'CREATED',
    AWAITING_MANAGER_PUBLISH: 'APPROVED',
    AWAITING_EMPLOYEE_ACKNOWLEDGEMENT: 'PUBLISHED_TO_EMPLOYEE',
  };

  const selectedCompanies = useSelectDisciplinaryActionsSelectedCompanies();
  const selectedLocations = useSelectDisciplinaryActionsSelectedLocations();

  const filteredRows = useMemo(() => {
    if (checkEnableCompanySupport()) {
      const selectedCompaniesMap = {};
      const selectedLocationsMap = {};
      selectedCompanies?.forEach((company) => {
        selectedCompaniesMap[company.id] = company;
      });
      selectedLocations?.forEach((location) => {
        selectedLocationsMap[location?.toLowerCase()] = location;
      });

      return rows?.filter(
        (row) => selectedCompaniesMap[row?.COMPANY?.id] && selectedLocationsMap[row?.LOCATION?.toLowerCase()]
      );
    }
    return rows;
  }, [rows, selectedCompanies, selectedLocations]);

  const { awaitingAdminApprovalCount, awaitingManagerPublishCount, awaitingEmployeeAcknowledgementCount } =
    useMemo(() => {
      let awaitingAdminApproval = 0;
      let awaitingManagerPublish = 0;
      let awaitingEmployeeAcknowledgement = 0;

      filteredRows?.forEach((rowData) => {
        switch (rowData?.STATUS) {
          case METRIC_CARD_HEADER_STATUS_VALUE_MAP.AWAITING_ADMIN_APPROVAL:
            awaitingAdminApproval++;
            break;
          case METRIC_CARD_HEADER_STATUS_VALUE_MAP.AWAITING_MANAGER_PUBLISH:
            awaitingManagerPublish++;
            break;
          case METRIC_CARD_HEADER_STATUS_VALUE_MAP.AWAITING_EMPLOYEE_ACKNOWLEDGEMENT:
            awaitingEmployeeAcknowledgement++;
            break;
          default:
            break;
        }
      });

      return {
        awaitingAdminApprovalCount: awaitingAdminApproval,
        awaitingManagerPublishCount: awaitingManagerPublish,
        awaitingEmployeeAcknowledgementCount: awaitingEmployeeAcknowledgement,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredRows]);

  const cardKPIData = [
    {
      cardId: DISCIPLINARY_ACTIONS_METRIC_CARDS.awaitingAdminApproval,
      title: 'Disciplinary actions awaiting admin confirmation',
      value: awaitingAdminApprovalCount,
    },
    {
      cardId: DISCIPLINARY_ACTIONS_METRIC_CARDS.awaitingManagerPublish,
      title: 'Disciplinary actions awaiting publish to employee',
      value: awaitingManagerPublishCount,
    },
    {
      cardId: DISCIPLINARY_ACTIONS_METRIC_CARDS.awaitingEmployeeAcknowledgement,
      title: "Disciplinary actions awaiting employee's acknowledgement",
      value: awaitingEmployeeAcknowledgementCount,
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex">
        <Skeleton height="100px" width="100%" borderRadius="8px" startColor="surface.F5F5F5" endColor="surface.g3" />
      </Box>
    );
  }

  if (error) {
    return (
      <StyledPaper padding="1rem" topGutter={3} elevation={0} showBorder>
        <StyledTypography variant="body2" color="red" align="center">
          Error occurred while fetching metric data
        </StyledTypography>
      </StyledPaper>
    );
  }

  return (
    <MetricCards
      height="100%"
      columns={`repeat(${cardKPIData?.length}, 1fr)`}
      cards={cardKPIData}
      module={MODULE_KEYS_METRIC_CARDS_DEER.DISCIPLINARY_ACTIONS}
      gridStatePersistenceKey={gridStatePersistenceKey}
      cardTitle="Metric (last 90 days)"
      cardStyle="style1"
    />
  );
};

export default MetricCardFilters;
